import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useHasLicense } from "../contexts/LicenseContext";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "../components/Navigation";
import RansomwarePreScanPanel from "../components/RansomwarePreScanPanel";
import IntelligenceNarrative from "../components/IntelligenceNarrative";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import Button from "../components/Buttons";
import { CheckCircle, AlertTriangle, Shield, X, Search, Edit, ChevronDown, ChevronUp } from "lucide-react";
import ReactSpeedometer from "react-d3-speedometer";
import { RiskScoreFootnote } from "./RiskScoreCards";
import { countActiveRisks } from "./shared/riskStatus";
import { severityPostureScore, severityPostureLevel } from '../utils/riskScoringEngine';
import { getComplianceColor, getComplianceBarColor, RISK_GAUGE_SEGMENT_STOPS, RISK_GAUGE_SEGMENT_COLORS } from '../utils/riskColors';
import { useLiveDashboard } from '../hooks/useLiveDashboard';

const org = JSON.parse(localStorage.getItem("selectedOrganization"));
//console.log("Selected Organization is:", org);
// User Management Component
const Dashboard = () => {
    const [analytics, setAnalytics] = useState(null);

    const apiCall = useApi();
    const { user, logout } = useAuth();
    const hasLicense = useHasLicense();
    const showDeals = hasLicense('Deals');
    const { canViewOrganizationAssessments, canManageOrganizationRisks } = useRBAC();

    const [organizationAnalytics, setOrganizationAnalytics] = useState(null);
    const [appsWithRisks, setAppsWithRisks] = useState([]);
    const [loadingZeroTrust, setLoadingZeroTrust] = useState(true);
    const [loading, setLoading] = useState(true);
    const [preScanOpen, setPreScanOpen] = useState(false);

    // Phase Coverage Compliance data
    const [complianceData, setComplianceData] = useState({
        compliance_status: {},
        overall_compliance: 0,
    });
    const [loadingCompliance, setLoadingCompliance] = useState(true);

    // Predictive Risk Timeline data
    const [predictiveRisk, setPredictiveRisk] = useState({
        day45: 0,
        day90: 0,
        day180: 0,
    });
    const [loadingPredictive, setLoadingPredictive] = useState(true);

    // Raw (unscaled) values — scaled by remediation ratio in derived useEffect
    const [rawCompliance, setRawCompliance] = useState({ compliance_status: {}, overall_compliance: 0 });
    const [rawPredictive, setRawPredictive] = useState({ day45: 0, day90: 0, day180: 0 });

    useEffect(() => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        // console.log("LocalStorage inside Dashboard:", org);
    }, []);

    // Load all dashboard data. `silent` skips the loading-state toggles so the live background
    // refresh swaps numbers in place with no spinner/flicker; the mount call passes silent=false for
    // the normal first-load spinners. The independent fetches run in parallel.
    const refreshData = useCallback(async (silent = false) => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (!org?.organizationId) return;

        const fetchOrgAnalytics = async () => {
            if (!silent) setLoading(true);
            try {
                const dist = await apiCall('GET', `/ransomwareRisks/vulnerability-distribution`);
                const counts = dist.risk_distribution || {};
                const allRisksCount = dist.all_risks || dist.total_risks || 0;
                const activeRisksCount = dist.total_risks || 0;
                const remediatedCount = allRisksCount - activeRisksCount;
                const score = severityPostureScore(counts);
                const level = severityPostureLevel(score);

                setOrganizationAnalytics({
                    overall_risk_score: score,
                    overall_RiskLevel: level,
                    total_risks: allRisksCount,
                    active_risks: activeRisksCount,
                    risk_distribution: counts,
                    remediated_risks: remediatedCount
                });

            } catch (err) {
                //console.error("❌ Error fetching analytics", err);
                setOrganizationAnalytics({ overall_risk_score: 0, overall_RiskLevel: 'LOW', total_risks: 0, active_risks: 0, risk_distribution: {}, remediated_risks: 0 });
            } finally {
                if (!silent) setLoading(false);
            }
        };

        const fetchZeroTrust = async () => {
            try {
                const data = await apiCall("GET", `/ransomwareRisks/zero-trust-scores`);

                setAnalytics(prev => ({ ...prev, zero_trust_readiness: data }));
            } catch (err) {}
        };

        const fetchCompliance = async () => {
            if (!silent) setLoadingCompliance(true);
            try {
                const data = await apiCall("GET", `/ransomwareRisks/risk-status?useCoverageStage=true`);
                setRawCompliance({
                    compliance_status: data?.risk_status || {},
                    overall_compliance: data?.overall_risk || 0,
                });
            } catch (err) {
                setRawCompliance({ compliance_status: {}, overall_compliance: 0 });
            } finally {
                if (!silent) setLoadingCompliance(false);
            }
        };

        const fetchPredictiveRisk = async () => {
            if (!silent) setLoadingPredictive(true);
            try {
                const data = await apiCall("GET", `/ransomwareRisks/organization-predictive-risk`);
                setRawPredictive({
                    day45: data?.predictive45DayScore || 0,
                    day90: data?.predictive90DayScore || 0,
                    day180: data?.predictive180DayScore || 0,
                });
            } catch (err) {
                setRawPredictive({ day45: 0, day90: 0, day180: 0 });
            } finally {
                if (!silent) setLoadingPredictive(false);
            }
        };

        await Promise.all([
            fetchOrgAnalytics(),
            fetchZeroTrust(),
            fetchCompliance(),
            fetchPredictiveRisk(),
        ]);
    }, [apiCall]);

    // First load (with spinners), then keep the dashboard live: the change-token poll re-runs the
    // fetch silently whenever risks/remediations change, with no visible loading state.
    useEffect(() => { refreshData(false); }, [refreshData]);
    const liveRefresh = useCallback(() => refreshData(true), [refreshData]);
    useLiveDashboard({ refresh: liveRefresh });

    // Derived: pass through risk-table-based data (already excludes remediated risks)
    useEffect(() => {
        setComplianceData({
            compliance_status: Object.fromEntries(
                Object.entries(rawCompliance.compliance_status || {}).map(([k, v]) => [k, v || 0])
            ),
            overall_compliance: rawCompliance.overall_compliance || 0,
        });
        setPredictiveRisk({
            day45: rawPredictive.day45,
            day90: rawPredictive.day90,
            day180: rawPredictive.day180,
        });
    }, [rawCompliance, rawPredictive]);

    const fetchDashboardAnalytics = async () => {
        try {
            //const data = await apiCall('GET', '/dashboard/risk-analytics');

            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                //console.error("No organization selected.");
                return;
            }

            //console.log("🔍 Fetching analytics for Org:", org.organizationId, org.organizationName);

            const risks = await apiCall('GET', `/Risks/organizational-analyticsRisks/${org.organizationId}`);

            // Add fallback values for missing properties
            const enhancedData = {
                ...risks,
                average_risk_value: risks.total_monetary_impact && risks.total_risks > 0
                    ? Math.round(risks.total_monetary_impact / risks.total_risks)
                    : 150000,
                trend_data: risks.trend_data || {
                    risk_trend: 'stable',
                    compliance_trend: 'improving'
                },
                recent_activities: risks.recent_activities || [
                    { type: 'assessment', title: 'Security Assessment Completed', time: '2 hours ago', status: 'success' },
                    { type: 'risk', title: 'Risk Analysis Updated', time: '4 hours ago', status: 'info' },
                    { type: 'compliance', title: 'Compliance Check Passed', time: '1 day ago', status: 'success' },
                    { type: 'update', title: 'Risk Register Updated', time: '2 days ago', status: 'info' }
                ]
            };

            setAnalytics(enhancedData);
        } catch (error) {
            //console.error('Error fetching dashboard analytics:', error);
            // Set fallback data in case of API error
            setAnalytics({
                overall_risk_score: 68,
                overall_RiskLevel: 'MEDIUM',
                risk_distribution: { CRITICAL: 3, HIGH: 8, MEDIUM: 15, LOW: 12, INFORMATIONAL: 5 },
                total_risks: 43,
                compliance_status: { 'SOC2': 85, 'ISO27001': 72, 'GDPR': 90, 'NIST': 68 },
                zero_trust_readiness: { Technology: 55, Backup: 76, App: 89, Detection: 71, Vendor: 59, Process: 87 },
                total_monetary_impact: 2500000,
                average_risk_value: 150000,
                trend_data: {
                    risk_trend: 'stable',
                    compliance_trend: 'improving'
                },
                last_updated: new Date().toISOString(),
                recent_activities: [
                    { type: 'assessment', title: 'Demo Assessment Completed', time: '2 hours ago', status: 'success' },
                    { type: 'risk', title: 'Demo Risk Updated', time: '4 hours ago', status: 'info' }
                ]
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />

            <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                <div className="mb-3">
                    <h1 className="text-3xl font-bold text-content-primary">
                        <span className="text-red-600">Ransomware Dashboard</span>
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Comprehensive organizational risk overview
                    </p>
                </div>

                {/* Intelligence Narrative — RISK_MANAGER+ at ORG only */}
                {canManageOrganizationRisks() && (
                    <IntelligenceNarrative
                        type="ransomware"
                        title="Intelligence Narrative"
                        subtitle="AI-generated briefing based on ransomware risks"
                        accentColor="rose"
                    />
                )}

                <div>
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                            <OverallRiskScoreCard analytics={organizationAnalytics} />
                            <PhaseCoverageComplianceCard complianceData={complianceData} analytics={organizationAnalytics} loading={loadingCompliance} />
                            <VulnerabilityDistributionCard analytics={organizationAnalytics} />
                            <PredictiveRiskTimelineCard predictiveRisk={predictiveRisk} loading={loadingPredictive} analytics={organizationAnalytics} />
                        </div>
                    )}
                </div>

                {/* Pre-Assessment Intelligence Scan — collapsible, closed by default */}
                <div className="mb-4">
                    <button
                        onClick={() => setPreScanOpen(o => !o)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <span className="text-sm font-semibold text-gray-700">Pre-Assessment Intelligence Scan</span>
                        {preScanOpen
                            ? <ChevronUp className="h-4 w-4 text-gray-400" />
                            : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>
                    {preScanOpen && (
                        <div className="mt-2">
                            <RansomwarePreScanPanel />
                        </div>
                    )}
                </div>

                {/* Organization Assessment - Ransomware */}
                {canViewOrganizationAssessments() && (
                    <div className="mb-4">
                        <RansomwareOrganizationAssessmentCard />
                    </div>
                )}

                {/* Business Units */}
                {showDeals && (
                    <div className="mb-4">
                        <RecentBUsCard />
                    </div>
                )}

                {/* Top 5 Recent Deals - Tile Format - Moved above Remediation Tracker */}
                {showDeals && (
                    <div className="mb-4">
                        <RecentDealsCard />
                    </div>
                )}

                {/* Remediation Status Tracker - Below Recent Deals */}
                <div className="mb-4">
                    <RemediationStatusTracker />
                </div>

                {/* Recent Activities - hidden until dynamic data is implemented */}
                {/* <div className="mb-4">
                    <RecentActivityCard analytics={analytics} />
                </div> */}
            </div>
        </div>
    );
};

// Ransomware Organization Assessment Card - DarkWeb style
const RansomwareOrganizationAssessmentCard = () => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [loading, setLoading] = useState(true);

    const apiCall = useApi();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Fetch organizations on mount
    useEffect(() => {
        fetchOrganizations(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Calculate risk score from counts
    const calculateRiskScore = (riskCounts = {}) => {
        return severityPostureScore(riskCounts);
    };

    const getRiskLevelFromScore = (score) => severityPostureLevel(score);

    const getRiskColor = (riskLevel) => {
        switch (riskLevel) {
            case "CRITICAL": return "bg-red-700";
            case "HIGH": return "bg-orange-500";
            case "MEDIUM": return "bg-yellow-500";
            case "LOW": return "bg-green-500";
            default: return "bg-gray-400";
        }
    };

    // Fetch organizations. `silent` skips the loading toggle so the live background refresh updates
    // the card in place with no spinner.
    const fetchOrganizations = useCallback(async (silent = false) => {
        try {
            const data = await apiCall("GET", `/RansomwareOrganization/summary`);
            setOrganizations(data);

            if (data.length > 0) {
                setSelectedOrg(data[0]);
                // Fetch assessment for each organization
                data.forEach((org) => fetchOrgAssessment(org.organizationId));
            }
        } catch (err) {} finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    // Fetch assessment for a specific organization (Following AI Dashboard pattern)
    const fetchOrgAssessment = async (orgId) => {
        try {
            // 1️⃣ Get assessments for this organization
            const assessments = await apiCall(
                "GET",
                `/ransomwareAssessments?organizationId=${orgId}`
            );

            if (!assessments || assessments.length === 0) {
                setOrganizations((prev) =>
                    prev.map((org) =>
                        org.organizationId === orgId
                            ? { ...org, risks: [], riskCounts: {}, riskScore: 0, hasAssessment: false, latestAssessment: null }
                            : org
                    )
                );
                return;
            }

            // 2️⃣ Pick the latest assessment
            const latestAssessment = assessments
                .sort((a, b) => new Date(b.ransomwareCompletedDate || b.createdAt) - new Date(a.ransomwareCompletedDate || a.createdAt))
            [0];

            // 3️⃣ Fetch risks for the latest assessment separately
            let risks = [];
            if (latestAssessment?.ransomwareAssessmentID) {
                risks = await apiCall(
                    "GET",
                    `/ransomwareAssessments/${latestAssessment.ransomwareAssessmentID}/risks`
                );
            }

            // 4️⃣ Calculate risk counts
            const normalize = (r) =>
                (r?.userRating || r?.residualRiskRating || r?.inherentRiskRating || "").toUpperCase();

            const riskCounts = {
                CRITICAL: risks.filter((r) => normalize(r) === "CRITICAL").length,
                HIGH: risks.filter((r) => normalize(r) === "HIGH").length,
                MEDIUM: risks.filter((r) => normalize(r) === "MEDIUM").length,
                LOW: risks.filter((r) => normalize(r) === "LOW").length,
            };

            const riskScore = calculateRiskScore(riskCounts);

            // Fetch active risk count (excluding remediated)
            let activeRisksCount = risks.length;
            try {
                const dist = await apiCall('GET', `/ransomwareRisks/vulnerability-distribution/assessment/${latestAssessment.ransomwareAssessmentID}`);
                activeRisksCount = dist.total_risks ?? risks.length;
            } catch {}

            // 5️⃣ Update organization state
            setOrganizations((prev) =>
                prev.map((org) =>
                    org.organizationId === orgId
                        ? {
                            ...org,
                            risks,
                            riskCounts,
                            totalRisks: risks.length,
                            activeRisksCount,
                            riskScore,
                            hasAssessment: !!latestAssessment,
                            latestAssessment
                        }
                        : org
                )
            );
        } catch (err) {
            setOrganizations((prev) =>
                prev.map((org) =>
                    org.organizationId === orgId
                        ? { ...org, risks: [], riskCounts: {}, riskScore: 0, hasAssessment: false, latestAssessment: null }
                        : org
                )
            );
        }
    };

    // Keep this card live alongside the rest of the dashboard (it fetches independently).
    const liveRefreshOrgs = useCallback(() => fetchOrganizations(true), [fetchOrganizations]);
    useLiveDashboard({ refresh: liveRefreshOrgs });

    // Show orgs that either have an assessment OR carry tagged risks (e.g. Enzoic) with no assessment,
    // so those tagged risks remain reachable via "View Risks".
    const orgsWithAssessment = organizations.filter(org => org.hasAssessment || (org.totalRisks ?? 0) > 0);

    // Hide the entire card if there's nothing to show (after loading)
    if (!loading && orgsWithAssessment.length === 0) {
        return null;
    }

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Organization Assessment</h3>
                <button
                    onClick={() => navigate("/RansomwareOrganizations")}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    View →
                </button>
            </div>

            {/* Show spinner while loading organizations */}
            {loading ? (
                <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {orgsWithAssessment.map((org) => {
                        const score = org.riskScore || 0;
                        const level = getRiskLevelFromScore(score);

                        return (
                            <div
                                key={org.organizationId}
                                className={`p-4 border rounded-lg cursor-pointer transition ${selectedOrg?.organizationId === org.organizationId
                                    ? "border-blue-500 shadow"
                                    : "border-gray-300"
                                    }`}
                                onClick={() => setSelectedOrg(org)}
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-semibold text-content-primary truncate">
                                        {org.organizationName}
                                    </h4>

                                    {(org.latestAssessment?.ransomwareAssessmentID && org.latestAssessment?.ransomwareStatus === "completed") ? (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            className="px-2 py-1 h-8 text-xs flex items-center"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/RansomwareAssessment/${org.latestAssessment.ransomwareAssessmentID}/risks`);
                                            }}
                                        >
                                            <Shield className="h-4 w-4 mr-1" />
                                            View Risks ({countActiveRisks(org.risks)})
                                        </Button>
                                    ) : (org.totalRisks ?? 0) > 0 ? (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            className="px-2 py-1 h-8 text-xs flex items-center"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // No org assessment, but org-level (tagged) Ransomware risks exist
                                                navigate(`/risks?types=Ransomware&scope=org`);
                                            }}
                                        >
                                            <Shield className="h-4 w-4 mr-1" />
                                            View Risks ({org.activeRisks ?? org.totalRisks})
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="px-2 py-1 h-8 text-xs flex items-center"
                                            disabled
                                        >
                                            <Shield className="h-4 w-4 mr-1" />
                                            No Risks
                                        </Button>
                                    )}
                                </div>

                                <p className="text-xs text-gray-500">Industry: {org.industry}</p>
                                <p className="text-xs text-gray-500">Region: {org.regionName}</p>

                                <div className="mt-2">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">Risk Level</span>
                                        <span className="text-xs font-medium">{level}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className={`h-1.5 rounded-full ${getRiskColor(level)}`}
                                            style={{ width: `${score}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

// Overall Risk Score Card with Donut Chart
const OverallRiskScoreCard = ({ analytics }) => {
    const { overall_risk_score = 0, overall_RiskLevel = 'LOW', active_risks = 0, total_risks = 0 } = analytics || {};
    const hasAssessment = total_risks > 0;

    const getRiskColor = (level) => {
        switch (level) {
            case 'CRITICAL': return 'text-red-700 bg-red-100';
            case 'HIGH': return 'text-orange-600 bg-orange-100';
            case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
            case 'LOW': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Ransomware Risk Score</h3>
            </div>

            <div className="w-full flex justify-center mb-2">
                <ReactSpeedometer
                    maxValue={100}
                    value={hasAssessment ? overall_risk_score : 0}
                    needleColor="#1f2937"
                    customSegmentStops={RISK_GAUGE_SEGMENT_STOPS}
                    segmentColors={RISK_GAUGE_SEGMENT_COLORS}
                    segments={RISK_GAUGE_SEGMENT_COLORS.length}
                    width={250}
                    height={150}
                    ringWidth={25}
                    needleTransitionDuration={1000}
                    needleTransition="easeElastic"
                    currentValueText={hasAssessment ? `${overall_risk_score}%` : " "}
                    needleHeightRatio={0.5}
                />
            </div>

            {hasAssessment ? (
                <>
                    <div className="flex justify-center space-x-2 mb-2">
                        <div className={`text-xs px-2 py-1 rounded ${getRiskColor(overall_RiskLevel)}`}>
                            {overall_RiskLevel}
                        </div>
                    </div>
                    <div className="text-xs text-gray-500">{active_risks} Active Risks</div>
                    <RiskScoreFootnote />
                </>
            ) : (
                <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
            )}
        </Card>
    );
};

// Compliance-percentage colors come from ../utils/riskColors (shared source of truth).

// Stage Explorer Card — grouped by attack stage
// Note: the backend also returns an "External" bucket for cross-listed/tagged Ransomware risks with
// no linked assessment question (so no resolvable CoverageStage). It's intentionally omitted here so
// the Stage Explorer shows only the three attack stages; those risks still count toward the overall
// weighted figure (see backend GetOrgRiskStatusWeightedUnifiedAsync).
const STAGE_ORDER = ['Pre-Attack', 'Active Attack', 'Post-Attack'];

const getDotColor = (value) => {
    if (value > 75) return 'bg-red-700';
    if (value > 55) return 'bg-red-500';
    if (value > 35) return 'bg-orange-500';
    if (value > 15) return 'bg-yellow-500';
    if (value > 0)  return 'bg-green-500';
    return 'bg-gray-300';
};

const PhaseCoverageComplianceCard = ({ complianceData, analytics, loading }) => {
    if (loading) {
        return (
            <Card className="text-center">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Stage Explorer
                </h3>
                <div className="text-sm text-gray-500 py-6">Loading...</div>
            </Card>
        );
    }

    const complianceStatus = complianceData?.compliance_status || {};
    const hasAssessment = (analytics?.total_risks || 0) > 0;

    const stageData = STAGE_ORDER.map(stage => {
        const apiKey = Object.keys(complianceStatus).find(k => k.toLowerCase() === stage.toLowerCase());
        return { stage, value: apiKey != null ? complianceStatus[apiKey] : null };
    });

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Stage Explorer</h3>
            </div>

            {hasAssessment ? (
                <>
                    <div className="space-y-3">
                        {stageData.map(({ stage, value }) => {
                            const pct = value ?? 0;
                            return (
                                <div key={stage} className="text-xs">
                                    <div className="flex justify-between items-center mb-1">
                                        <div className="flex items-center gap-1.5">
                                            <div className={`w-2 h-2 rounded-full ${value != null ? getDotColor(pct) : 'bg-gray-300'}`} />
                                            <span className="text-gray-600 font-medium">{stage}</span>
                                        </div>
                                        <span className={`font-semibold ${value != null ? getComplianceColor(pct).split(" ")[0] : 'text-gray-400'}`}>
                                            {value != null ? `${pct}%` : '—'}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className={`${getComplianceBarColor(pct)} h-1.5 rounded-full transition-all`}
                                            style={{ width: value != null ? `${pct}%` : '0%' }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <>
                    <div className="mb-4">
                        <div className="text-3xl font-bold text-gray-300">—</div>
                    </div>
                    <div className="space-y-3">
                        {STAGE_ORDER.map(stage => (
                            <div key={stage} className="text-xs">
                                <div className="flex justify-between items-center mb-1">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-gray-200" />
                                        <span className="text-gray-400">{stage}</span>
                                    </div>
                                    <span className="text-gray-300">—</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-1.5" />
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 text-xs text-gray-400 italic">Assessment Not Started</div>
                </>
            )}
        </Card>
    );
};

// Vulnerability Distribution Card
const VulnerabilityDistributionCard = ({ analytics }) => {
    const { risk_distribution = {}, total_risks = 0, active_risks = 0, remediated_risks = 0 } = analytics || {};
    const hasAssessment = total_risks > 0;

    if (!hasAssessment) {
        return (
            <Card className="text-center">
                <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Vulnerability Distribution</h3>
                </div>
                <div className="space-y-2 mb-4">
                    {['Critical', 'High', 'Medium', 'Low'].map(label => (
                        <div key={label} className="flex justify-between items-center opacity-30">
                            <div className="flex items-center">
                                <div className="w-3 h-3 bg-gray-300 rounded-full mr-2"></div>
                                <span className="text-sm font-medium text-gray-400">{label}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-300">0</span>
                        </div>
                    ))}
                </div>
                <div className="border-t pt-2 space-y-1">
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">Total Risks</span>
                        <span className="text-sm font-bold text-gray-300">0</span>
                    </div>
                </div>
                <div className="mt-3 text-xs text-gray-400 italic">Assessment Not Started</div>
            </Card>
        );
    }

    const criticalCount = risk_distribution.CRITICAL || 0;
    const highCount = risk_distribution.HIGH || 0;
    const mediumCount = risk_distribution.MEDIUM || 0;
    const lowCount = risk_distribution.LOW || 0;
    const infoCount = risk_distribution.INFORMATIONAL || 0;

    const { overall_risk_score = 0, overall_RiskLevel = 'LOW' } = analytics || {};
    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Vulnerability Distribution</h3>
            </div>

            {/* Show all risk levels with counts */}
            <div className="space-y-2 mb-4">
                {criticalCount > 0 && (
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-700 rounded-full mr-2"></div>
                            <span className="text-sm font-medium">Critical</span>
                        </div>
                        <span className="text-sm font-bold text-red-700">{criticalCount}</span>
                    </div>
                )}
                {highCount > 0 && (
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium">High</span>
                        </div>
                        <span className="text-sm font-bold text-orange-500">{highCount}</span>
                    </div>
                )}
                {mediumCount > 0 && (
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium">Medium</span>
                        </div>
                        <span className="text-sm font-bold text-yellow-600">{mediumCount}</span>
                    </div>
                )}
                {lowCount > 0 && (
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium">Low</span>
                        </div>
                        <span className="text-sm font-bold text-green-600">{lowCount}</span>
                    </div>
                )}
                {infoCount > 0 && (
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                            <span className="text-sm font-medium">Info</span>
                        </div>
                        <span className="text-sm font-bold text-blue-600">{infoCount}</span>
                    </div>
                )}
            </div>

            {/* Risk counts summary */}
            <div className="border-t pt-2 space-y-1">
                <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Total Risks</span>
                    <span className="text-sm font-bold text-gray-800">{total_risks}</span>
                </div>
                {remediated_risks > 0 && (
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-green-600">Remediated</span>
                        <span className="text-sm font-bold text-green-600">{remediated_risks}</span>
                    </div>
                )}
                <div className="flex justify-between items-center">
                    <span className="text-xs text-blue-600">Active Risks</span>
                    <span className="text-sm font-bold text-blue-600">{active_risks}</span>
                </div>
            </div>
        </Card>
    );
};

// Predictive Risk Timeline Card
const PredictiveRiskTimelineCard = ({ predictiveRisk, loading, analytics }) => {
    if (loading) {
        return (
            <Card className="text-center">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Predictive Risk Timeline
                </h3>
                <div className="text-sm text-gray-500 py-6">Loading...</div>
            </Card>
        );
    }

    const { day45 = 0, day90 = 0, day180 = 0 } = predictiveRisk || {};
    const hasAssessment = (analytics?.total_risks || 0) > 0;

    const horizons = [
        { label: '45 Days', value: hasAssessment ? day45 : 0, color: 'text-yellow-700', bar: 'bg-yellow-500' },
        { label: '90 Days', value: hasAssessment ? day90 : 0, color: 'text-orange-700', bar: 'bg-orange-500' },
        { label: '180 Days', value: hasAssessment ? day180 : 0, color: 'text-red-700', bar: 'bg-red-600' },
    ];

    return (
        <Card className="text-center">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                Predictive Risk Timeline
            </h3>
            <p className="text-xs text-gray-400 mb-4">Risk score forecast · not financial</p>

            <div className="space-y-3">
                {horizons.map(({ label, value, color, bar }) => (
                    <div key={label}>
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600">{label}</span>
                            {hasAssessment
                                ? <span className={`font-medium ${color}`}>{value}%</span>
                                : <span className="text-gray-300">—</span>
                            }
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className={`${hasAssessment ? bar : 'bg-gray-200'} h-2 rounded-full transition-all duration-500`}
                                style={{ width: hasAssessment ? `${value}%` : '0%' }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 text-xs text-gray-500">
                {hasAssessment ? 'Risk escalation forecast' : <span className="italic text-gray-400">Assessment Not Started</span>}
            </div>
        </Card>
    );
};

// Smaller version of Stage Explorer chart for dashboard cards
const ZeroTrustSpiderChartSmall = ({ analytics }) => {
    const zeroTrustReadiness = analytics?.zero_trust_readiness || { Technology: 55, Backup: 76, App: 89, Detection: 71, Vendor: 59, Process: 87 };
    const categories = Object.entries(zeroTrustReadiness);
    const avgReadiness = categories.length > 0
        ? Math.round(categories.reduce((sum, [_, value]) => sum + value, 0) / categories.length)
        : 0;

    const getMaturityColor = (score) => {
        if (score >= 80) return '#10B981'; // Green
        if (score >= 60) return '#F59E0B'; // Yellow
        if (score >= 40) return '#F97316'; // Orange
        return '#EF4444'; // Red
    };
    // Stage Explorer Card
    if (categories.length === 0) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-4">Stage Explorer</h3>
                <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500 text-sm">No data</div>
                </div>
            </Card>
        );
    }

    const generateRadarPoints = (values) => {
        const centerX = 60;
        const centerY = 60;
        const maxRadius = 40;

        return values.map((value, index) => {
            const angle = (index * 2 * Math.PI) / values.length - Math.PI / 2;
            const radius = (value / 100) * maxRadius;
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            return `${x},${y}`;
        }).join(' ');
    };

    const values = categories.map(([_, value]) => value);
    const radarPoints = generateRadarPoints(values);

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">Stage Explorer</h3>

            <div className="flex flex-col items-center">
                {/* Overall Score */}
                <div className="text-center mb-3">
                    <div className="text-2xl font-bold" style={{ color: getMaturityColor(avgReadiness) }}>
                        {avgReadiness}%
                    </div>
                    <div className="text-xs text-gray-600">Readiness</div>
                </div>

                {/* Mini Radar Chart */}
                <svg width="120" height="120" className="mb-3">
                    {/* Grid circles */}
                    {[25, 50, 75, 100].map(percent => (
                        <circle
                            key={percent}
                            cx="60"
                            cy="60"
                            r={(percent / 100) * 40}
                            fill="none"
                            stroke="#E5E7EB"
                            strokeWidth="0.5"
                        />
                    ))}

                    {/* Grid lines */}
                    {categories.map((_, index) => {
                        const angle = (index * 2 * Math.PI) / categories.length - Math.PI / 2;
                        const x = 60 + 40 * Math.cos(angle);
                        const y = 60 + 40 * Math.sin(angle);
                        return (
                            <line
                                key={index}
                                x1="60"
                                y1="60"
                                x2={x}
                                y2={y}
                                stroke="#E5E7EB"
                                strokeWidth="0.5"
                            />
                        );
                    })}

                    {/* Data polygon */}
                    <polygon
                        points={radarPoints}
                        fill={getMaturityColor(avgReadiness)}
                        fillOpacity="0.3"
                        stroke={getMaturityColor(avgReadiness)}
                        strokeWidth="1.5"
                    />

                    {/* Data points */}
                    {categories.map(([_, value], index) => {
                        const angle = (index * 2 * Math.PI) / categories.length - Math.PI / 2;
                        const radius = (value / 100) * 40;
                        const x = 60 + radius * Math.cos(angle);
                        const y = 60 + radius * Math.sin(angle);
                        return (
                            <circle
                                key={index}
                                cx={x}
                                cy={y}
                                r="2"
                                fill={getMaturityColor(value)}
                            />
                        );
                    })}
                </svg>

                {/* Compact Legend */}
                <div className="space-y-1 text-xs w-full">
                    {categories.slice(0, 7).map(([category, value]) => (
                        <div key={category} className="flex justify-between items-center">
                            <div className="flex items-center space-x-1">
                                <div
                                    className="w-2 h-2 rounded"
                                    style={{ backgroundColor: getMaturityColor(value) }}
                                />
                                <span className="text-gray-600 truncate">{category}</span>
                            </div>
                            <span className="font-medium">{value}%</span>
                        </div>
                    ))}
                    {categories.length > 7 && (
                        <div className="text-xs text-gray-500 text-center">
                            +{categories.length - 7} more
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

// Business Units Card
const RecentBUsCard = () => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const apiCall = useApi();
    const navigate = useNavigate();

    const calcBURiskScore = (riskLevel) => {
        switch ((riskLevel || '').toUpperCase()) {
            case 'CRITICAL': return 95; case 'HIGH': return 80;
            case 'MEDIUM': return 60; case 'LOW': return 30; default: return 0;
        }
    };
    const getRiskLevelFromScore = (score) => severityPostureLevel(score);
    const getRiskColor = (level) => ({ CRITICAL: "bg-red-700", HIGH: "bg-orange-500", MEDIUM: "bg-yellow-500", LOW: "bg-green-500" }[level] || "bg-gray-400");

    const fetchBusinessUnits = useCallback(async (silent = false) => {
        try {
            const data = await apiCall("GET", "/businessUnits/summary?category=Ransomware&pageSize=5");
            setBusinessUnits((data.items || []).map(bu => ({
                ...bu,
                riskCounts: { CRITICAL: bu.riskCounts?.critical || 0, HIGH: bu.riskCounts?.high || 0, MEDIUM: bu.riskCounts?.medium || 0, LOW: bu.riskCounts?.low || 0 },
                totalRisks: (bu.riskCounts?.critical || 0) + (bu.riskCounts?.high || 0) + (bu.riskCounts?.medium || 0) + (bu.riskCounts?.low || 0),
                riskScore: calcBURiskScore(bu.riskLevel),
                hasAssessment: (bu.applicationCount || 0) > 0,
            })));
        } catch { } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchBusinessUnits(false);
    }, [fetchBusinessUnits]);

    // Keep this card live alongside the rest of the dashboard (it fetches independently).
    const liveRefreshBUs = useCallback(() => fetchBusinessUnits(true), [fetchBusinessUnits]);
    useLiveDashboard({ refresh: liveRefreshBUs });

    if (!loading && businessUnits.length === 0) return null;

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Business Units</h3>
                <button onClick={() => navigate("/RansomwareBusinessUnits")} className="text-blue-600 hover:text-blue-800 text-sm font-medium">View All →</button>
            </div>
            {loading ? (
                <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {businessUnits.map((bu) => {
                        const score = bu.riskScore || 0; const level = getRiskLevelFromScore(score);
                        const hasRisk = bu.hasAssessment && bu.totalRisks > 0;
                        const badgeClass = level === 'CRITICAL' ? 'bg-red-100 text-red-700' : level === 'HIGH' ? 'bg-orange-100 text-orange-600' : level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600';
                        const scoreTextClass = level === 'CRITICAL' ? 'text-red-700' : level === 'HIGH' ? 'text-orange-600' : level === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
                        return (
                            <div key={bu.businessUnitId} className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300" onClick={() => navigate("/RansomwareBusinessUnits")}>
                                <div className={`h-1 ${hasRisk ? getRiskColor(level) : 'bg-gray-200'}`} />
                                <div className="p-4">
                                    <div className="flex justify-between items-start gap-2 mb-1.5">
                                        <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{bu.businessUnitName}</h4>
                                        {hasRisk && <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{level}</span>}
                                    </div>
                                    {bu.domain && <p className="text-xs text-gray-400 mb-2">🌐 {bu.domain}</p>}
                                    {bu.assignedToUserName && (
                                        <div className="flex items-center gap-1.5 mb-2">
                                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0"><span className="text-[10px] font-bold text-gray-500">{bu.assignedToUserName[0]?.toUpperCase()}</span></div>
                                            <span className="text-xs text-gray-500 truncate">{bu.assignedToUserName}</span>
                                        </div>
                                    )}
                                    {hasRisk ? (
                                        <div className="mb-2">
                                            <div className="flex justify-between text-xs mb-1"><span className="text-gray-400">Risk Score</span><span className={`font-bold ${scoreTextClass}`}>{score}%</span></div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${getRiskColor(level)}`} style={{ width: `${score}%` }} /></div>
                                        </div>
                                    ) : <div className="text-xs text-gray-300 italic mb-2">No assessment yet</div>}
                                    {hasRisk && (
                                        <div className="flex justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                                            {bu.riskCounts.CRITICAL > 0 && <span className="text-red-600 font-medium">{bu.riskCounts.CRITICAL} Critical</span>}
                                            {bu.riskCounts.HIGH > 0 && <span className="text-orange-500 font-medium">{bu.riskCounts.HIGH} High</span>}
                                            {bu.riskCounts.MEDIUM > 0 && <span className="text-yellow-600 font-medium">{bu.riskCounts.MEDIUM} Med</span>}
                                            {bu.riskCounts.LOW > 0 && <span className="text-green-600 font-medium">{bu.riskCounts.LOW} Low</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

// Recent Deals Card - Top 5 acquisitions in tile format for easy navigation
// Recent Deals Card - Top 5 deals in tile format for easy navigation
const RecentDealsCard = () => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    // ✅ add states for applications
    const [applications, setApplications] = useState([]);
    const [applicationsWithRisks, setApplicationsWithRisks] = useState([]);
    const [selectedDeal, setSelectedDeal] = useState(null);

    const apiCall = useApi();
    const navigate = useNavigate();

    const fetchDeals = useCallback(async (silent = false) => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            const category = "Ransomware";
            const data = await apiCall("GET", `/deals/all?category=${encodeURIComponent(category)}`);

            const sortedDeals = data
                .sort((a, b) => new Date(b.dealStartDate) - new Date(a.dealStartDate))
                .slice(0, 5)
                .map(deal => ({ ...deal, riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 }, riskScore: 0, hasAssessment: false }));

            setDeals(sortedDeals);
            sortedDeals.forEach(deal => fetchDealRisk(deal.dealID));
        } catch (error) {} finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchDeals(false);
    }, [fetchDeals]);

    const fetchDealRisk = async (dealID) => {
        try {
            const assessments = await apiCall("GET", `/ransomwareAssessments?dealId=${dealID}`);
            const latestAssessment = assessments?.[0] || null;
            let risks = [];
            if (latestAssessment?.ransomwareAssessmentID) {
                risks = await apiCall("GET", `/ransomwareAssessments/${latestAssessment.ransomwareAssessmentID}/risks`);
            }
            const normalize = (r) =>
                (r?.userRating || r?.residualRiskRating || r?.inherentRiskRating || "").toUpperCase();
            const riskCounts = {
                CRITICAL: risks.filter(r => normalize(r) === "CRITICAL").length,
                HIGH: risks.filter(r => normalize(r) === "HIGH").length,
                MEDIUM: risks.filter(r => normalize(r) === "MEDIUM").length,
                LOW: risks.filter(r => normalize(r) === "LOW").length,
            };
            const riskScore = calculateRiskScore(riskCounts);
            setDeals(prev => {
                const updated = prev.map(d =>
                    d.dealID === dealID ? { ...d, riskCounts, riskScore, hasAssessment: !!latestAssessment } : d
                );
                return updated.sort((a, b) => {
                    const aHasRisks = a.hasAssessment && Object.values(a.riskCounts).reduce((s, v) => s + v, 0) > 0;
                    const bHasRisks = b.hasAssessment && Object.values(b.riskCounts).reduce((s, v) => s + v, 0) > 0;
                    if (aHasRisks !== bHasRisks) return aHasRisks ? -1 : 1;
                    return new Date(b.dealStartDate) - new Date(a.dealStartDate);
                });
            });
        } catch (err) {}
    };


    // ✅ fetch applications for a given deal
    const fetchApplications = async (dealID) => {
        try {
            const data = await apiCall("GET", `/deals/${dealID}/applications`);
            setApplications(data);

            // add risks
            const appsWithRisks = await Promise.all(
                data.map(async (app) => {
                    try {
                        const assessments = await apiCall(
                            "GET",
                            `/assessments?applicationId=${app.applicationId}`
                        );
                        if (assessments.length > 0) {
                            const latest = assessments[0];

                            const risks = await apiCall(
                                "GET",
                                `/assessments/${latest.assessmentID}/risks`
                            );

                            const normalizeRating = (rating) =>
                                (rating || "").toString().toUpperCase();

                            const riskCounts = {
                                CRITICAL: risks.filter(
                                    (r) => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "CRITICAL"
                                ).length,
                                HIGH: risks.filter(
                                    (r) => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "HIGH"
                                ).length,
                                MEDIUM: risks.filter(
                                    (r) => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "MEDIUM"
                                ).length,
                                LOW: risks.filter(
                                    (r) => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "LOW"
                                ).length,
                            };

                            const totalRisks = risks.length;
                            const riskScore =
                                latest.overall_risk_score || calculateRiskScore(riskCounts);
                            return {
                                ...app,
                                assessmentId: latest.assessmentID,
                                risks,
                                riskCounts,
                                totalRisks,
                                riskScore,
                                hasAssessment: true,
                            };
                        }
                        return {
                            ...app,
                            assessmentId: null,
                            risks: [],
                            riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                            totalRisks: 0,
                            riskScore: 0,
                            hasAssessment: false,
                        };
                    } catch {
                        return {
                            ...app,
                            assessmentId: null,
                            risks: [],
                            riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                            totalRisks: 0,
                            riskScore: 0,
                            hasAssessment: false,
                        };
                    }
                })
            );


            setApplicationsWithRisks(appsWithRisks);

            setDeals((prev) =>
                prev.map((a) =>
                    a.dealID === dealID ? { ...a, applicationCount: data.length } : a
                )
            );
        } catch (error) {}
    };

    const calculateRiskScore = (riskCounts) => {
        return severityPostureScore(riskCounts);
    };


    const getRiskColor = (riskLevel) => {
        switch (riskLevel) {
            case "CRITICAL":
                return "bg-red-700";
            case "HIGH":
                return "bg-orange-500";
            case "MEDIUM":
                return "bg-yellow-500";
            case "LOW":
                return "bg-green-500";
            default:
                return "bg-gray-400";
        }
    };

    const getRiskPercentage = (riskLevel) => {
        switch (riskLevel) {
            case "CRITICAL":
                return 90;
            case "HIGH":
                return 75;
            case "MEDIUM":
                return 50;
            case "LOW":
                return 25;
            default:
                return 10;
        }
    };

    const getRiskLevelFromScore = (score) => severityPostureLevel(score);

    // Keep this card live alongside the rest of the dashboard (it fetches independently).
    const liveRefreshDeals = useCallback(() => fetchDeals(true), [fetchDeals]);
    useLiveDashboard({ refresh: liveRefreshDeals });

    if (loading) {
        return (
            <Card>
                <h3 className="text-lg font-semibold mb-4">Recent Deals</h3>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Card>
        );
    }

    if (deals.length === 0) return null;

    return (
        <Card>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Recent Deals</h3>
                <button
                    onClick={() => navigate("/RansomwareDeals")}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    View All →
                </button>
            </div>

            {deals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No deals found</p>
                </div>
            ) : (
                <>
                    {/* ✅ deals grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {deals.map((deal) => {
                            const riskLevel = getRiskLevelFromScore(deal.riskScore || 0);
                            const riskPct = getRiskPercentage(riskLevel);
                            const hasRisk = deal.hasAssessment && Object.values(deal.riskCounts || {}).reduce((a, b) => a + b, 0) > 0;
                            const badgeClass = riskLevel === 'CRITICAL' ? 'bg-red-100 text-red-700' : riskLevel === 'HIGH' ? 'bg-orange-100 text-orange-600' : riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600';
                            const scoreTextClass = riskLevel === 'CRITICAL' ? 'text-red-700' : riskLevel === 'HIGH' ? 'text-orange-600' : riskLevel === 'MEDIUM' ? 'text-yellow-600' : 'text-green-600';
                            return (
                            <div
                                key={deal.dealID}
                                className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                                onClick={() => navigate("/RansomwareDeals")}
                            >
                                <div className={`h-1 ${hasRisk ? getRiskColor(riskLevel) : 'bg-gray-200'}`} />
                                <div className="p-4">
                                    <div className="flex justify-between items-start gap-2 mb-1.5">
                                        <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{deal.dealName}</h4>
                                        {hasRisk && (
                                            <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{riskLevel}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 mb-3">📍 {deal.dealCity}, {deal.dealCountry}</p>
                                    <div className="flex items-center gap-1.5 mb-3">
                                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                            <span className="text-[10px] font-bold text-gray-500">{(deal.dealOwner || 'N')[0]?.toUpperCase()}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 truncate">{deal.dealOwner || 'N/A'}</span>
                                    </div>
                                    {hasRisk ? (
                                        <div className="mb-3">
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="text-gray-400">Risk Score</span>
                                                <span className={`font-bold ${scoreTextClass}`}>{deal.riskScore || 0}%</span>
                                            </div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${getRiskColor(riskLevel)}`} style={{ width: `${riskPct}%` }} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-xs text-gray-300 italic mb-3">No assessment yet</div>
                                    )}
                                    {(() => { const fmt = (d) => { const dt = d ? new Date(d) : null; return dt && !isNaN(dt) && dt.getFullYear() > 2000 ? dt.toLocaleDateString() : null; }; const s = fmt(deal.dealStartDate); const e = fmt(deal.dealEndDate); return (s || e) ? <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100"><span>{s || '—'}</span><span className="text-gray-300">→</span><span>{e || '—'}</span></div> : null; })()}
                                </div>
                            </div>
                        );
                        })}
                    </div>

                    {/* ✅ show applications when a deal is clicked */}
                    {/* 
                    {selectedDeal && (
                        <div className="mt-6">
                            <h2 className="text-lg font-bold mb-2">
                                Applications for {selectedDeal.dealName}
                            </h2>
                            {applicationsWithRisks.length === 0 ? (
                                <p className="text-gray-500">No applications found.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {applicationsWithRisks.map((app) => (
                                        <li
                                            key={app.applicationId}
                                            className="p-2 border rounded shadow-sm"
                                        >
                                            <div className="font-medium">{app.applicationName}</div>
                                            <div className="text-sm text-gray-500 mb-1">
                                                Risk Score: {app.riskScore}%
                                            </div>

                                            <div className="w-full bg-gray-200 rounded-full h-4">
                                                <div
                                                    className={`h-4 rounded-full ${app.riskScore <= 40
                                                        ? 'bg-green-500'
                                                        : app.riskScore <= 60
                                                            ? 'bg-yellow-500'
                                                            : app.riskScore <= 80
                                                                ? 'bg-orange-500'
                                                                : 'bg-red-500'
                                                        }`}
                                                    style={{ width: `${app.riskScore}%` }}
                                                ></div>
                                            </div>

                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                         */}
                </>
            )}
        </Card>
    );
};


// Main App Component
// RemediationStatusTracker Component for Dashboard
const RemediationStatusTracker = () => {
    const { user } = useAuth();
    const apiCall = useApi();  // Fix: useApi returns the function directly, not an object
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRemediationSummary = useCallback(async (silent = false) => {
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                return;
            }

            const response = await apiCall(
                'GET',
                `/ransomwareRemediations/summary`
            );
            setSummary(response);
        } catch (error) {
            setSummary({
                totalRemediations: 0,
                byStatus: [],
                byPriority: [],
                overdueCount: 0,
                notStartedCount: 0,
                inProgressCount: 0,
                openCount: 0,
                completedCount: 0,
                top_in_progress: []
            });
        } finally {
            if (!silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCall]);

    useEffect(() => {
        fetchRemediationSummary(false);
    }, [fetchRemediationSummary]);

    // Keep this card live alongside the rest of the dashboard (it fetches independently).
    const liveRefreshRemediation = useCallback(() => fetchRemediationSummary(true), [fetchRemediationSummary]);
    useLiveDashboard({ refresh: liveRefreshRemediation });

    if (loading) return <LoadingSpinner />;

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'CRITICAL': return 'text-red-700 bg-red-100';
            case 'HIGH': return 'text-orange-600 bg-orange-100';
            case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
            case 'LOW': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'text-green-600 bg-green-100';
            case 'In Progress': return 'text-blue-600 bg-blue-100';
            case 'Waiting': return 'text-yellow-600 bg-yellow-100';
            case 'Under Review': return 'text-purple-600 bg-purple-100';
            case 'Deferred': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <Card>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-content-primary flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-blue-600" />
                    Remediation Status Tracker
                </h3>
                <button
                    onClick={() => navigate('/remediations?types=Ransomware')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                >
                    View All →
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
                {/*
                <div className="text-center">
                    <div className="text-2xl font-bold text-content-primary">{summary.totalRemediations}</div>
                    <div className="text-sm text-gray-500">Total Remediations </div>
                </div>
                */}
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{summary.notStartedCount}</div>
                    <div className="text-sm text-gray-500">Not Started</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{summary.inProgressCount}</div>
                    <div className="text-sm text-gray-500">In Progress</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{summary.completedCount}</div>
                    <div className="text-sm text-gray-500">Completed</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{summary.overdueCount}</div>
                    <div className="text-sm text-gray-500">Overdue</div>
                </div>
            </div>

            {summary.top_in_progress && summary.top_in_progress.length > 0 && (
                <div>
                    <h4 className="font-medium text-content-primary mb-3">Top In-Progress Tasks</h4>
                    <div className="space-y-2">
                        {summary.top_in_progress.slice(0, 5).map((remediation) => (
                            <div key={remediation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                    <div className="font-medium text-content-primary truncate">{remediation.title}</div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        📱 {remediation.applicationName || 'Not Linked'} | 🏢 {remediation.acquisition_name || 'Not Linked'}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate mt-1">
                                        ⚠️ Related to: {remediation.risk_name || 'Not Linked'}
                                    </div>
                                    <div className="flex items-center space-x-2 mt-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(remediation.priority)}`}>
                                            {remediation.priority}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(remediation.status)}`}>
                                            {remediation.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium text-content-primary">
                                        {remediation.progress_percentage || 0}%
                                    </div>
                                    <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full"
                                            style={{ width: `${remediation.progress_percentage || 0}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};

// Remediation Update Modal Component
const RemediationUpdateModal = ({ remediation, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({
        status: remediation.status || 'Not Started',
        progress_percentage: remediation.progress_percentage || 0,
        assigned_to: remediation.assigned_to || '',
        comments: ''
    });
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const { user } = useAuth();
    const apiCall = useApi();

    // Fetch users for assignment dropdown
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await apiCall('GET', '/users');
            setUsers(response || []);
        } catch (error) {
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(remediation.id, formData);
    };

    const statusOptions = [
        'Not Started',
        'In Progress',
        'Waiting',
        'Under Review',
        'Completed',
    ];

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-content-primary">Update Remediation</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Progress Percentage: {formData.progress_percentage}%
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={formData.progress_percentage}
                                onChange={(e) => setFormData({ ...formData, progress_percentage: parseInt(e.target.value) })}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assigned To
                            </label>
                            <select
                                value={formData.assigned_to}
                                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                disabled={loadingUsers}
                            >
                                <option value="">Unassigned</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.full_name || u.email}
                                    </option>
                                ))}
                            </select>
                            {loadingUsers && (
                                <p className="text-sm text-gray-500 mt-1">Loading users...</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Comment (Optional)
                            </label>
                            <textarea
                                value={formData.comments}
                                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                placeholder="Add a comment about this update..."
                                rows="3"
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Update
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Recent Activity Card
const RecentActivityCard = ({ analytics }) => {
    const complianceStatus = analytics?.compliance_status || { 'SOC2': 85, 'ISO27001': 72, 'GDPR': 90, 'NIST': 68 };
    const activities = [
        {
            type: 'assessment_completion',
            title: 'New Assessment Completed',
            description: `${analytics?.recent_assessments || 0} assessments completed this month`,
            time: '2 hours ago',
            status: 'success'
        },
        {
            type: 'risk_update',
            title: 'Risk Rating Updated',
            description: 'Multiple risks reassessed and updated',
            time: '1 day ago',
            status: 'warning'
        },
        {
            type: 'compliance_check',
            title: 'Compliance Status Updated',
            description: `Framework compliance at ${Math.round(Object.values(complianceStatus).reduce((a, b) => a + b, 0) / Object.values(complianceStatus).length)}%`,
            time: '2 days ago',
            status: 'info'
        }
    ];

    const getActivityIcon = (type) => {
        switch (type) {
            case 'assessment_completion': return CheckCircle;
            case 'risk_update': return AlertTriangle;
            case 'compliance_check': return Shield;
            default: return Shield;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return 'text-green-600 bg-green-100';
            case 'warning': return 'text-yellow-600 bg-yellow-100';
            case 'info': return 'text-blue-600 bg-blue-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-4">
                {activities.map((activity, index) => {
                    const IconComponent = getActivityIcon(activity.type);
                    return (
                        <div key={index} className="flex items-start space-x-3">
                            <div className={`p-2 rounded-full ${getStatusColor(activity.status)}`}>
                                <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-content-primary">{activity.title}</p>
                                <p className="text-xs text-gray-500">{activity.description}</p>
                                <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};




export default Dashboard;
